import { useState, useLayoutEffect, useEffect, useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import "./SustainabilityAuditsPage.scss";
import { useDarkModeCheck } from "../DashboardLayout";
import FilterRow from "../../../component/filter-row/FilterRow";
import {
  genericDocumentsTable,
  SortOrder,
} from "../../../service/utils/constants";
import SortableTable, {
  SortSettings,
} from "../../../component/sortable-table/SortableTable";
import Container from "../../../component/container/Container";
import { sortTableFunction } from "../../../service/utils/functions";
import { GET_CUSTOMERS } from "../../../service/graphql/query";
import { CustomerDataEntry } from "../../../service/utils/interfaces";
import { auditsDataFormat } from "../../../service/utils/parsers";
import { logout } from "../../../service/graphql/utils";
import * as Storage from "../../../service/storage";

const SustainabilityAuditsPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useDarkModeCheck();

  const [headerLine, setHeaderLine] = useState<any[]>([]);
  const [content, setContent] = useState<any[][]>([]);
  const [sortSettings, setSortSettings] = useState<SortSettings>();
  const [searchParam, setSearchParam] = useState("");
  const [reordering, setReordering] = useState(false);

  const [customerList, setCustomerList] = useState<CustomerDataEntry[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>();

  const [width, setWidth] = useState(0);

  const [getCustomers, { data: userData }] = useLazyQuery(GET_CUSTOMERS, {
    errorPolicy: "all",
    onError: () => navigate("/login"),
  });

  useLayoutEffect(() => {
    const width = window.innerWidth;
    setWidth(width);
  }, []);

  const checkUserPermissions = useCallback(
    (customers: any[]) => {
      const user = customers[0];
      if (user) {
        if (user.category === "Tipping" || !user.pageAuth.sustainabilityAudit) {
          window.history.replaceState(null, "", "/");
          navigate("/dashboard/home");
        }
      } else logout(navigate);
    },
    [navigate]
  );

  useEffect(() => {
    Storage.USER_DATA.get()
      .then(async (customerList) => {
        if (customerList && customerList.length > 0) {
          checkUserPermissions(customerList);
          manageAuditsData(customerList);
        } else {
          getCustomers({ context: { apiName: "middleware" } });
        }
      })
      .catch(() => {
        getCustomers({ context: { apiName: "middleware" } });
      });
  }, [getCustomers, checkUserPermissions]);

  useEffect(() => {
    if (userData && userData.getCustomers && userData.getCustomers.length > 0) {
      checkUserPermissions(userData.getCustomers);
      manageAuditsData(userData.getCustomers);
    }
  }, [userData, checkUserPermissions]);

  useEffect(() => {
    setHeaderLine(genericDocumentsTable);

    const handleWindowResize = () => {
      const width = window.innerWidth;
      setWidth(width);
    };
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const manageAuditsData = async (auditsData: any) => {
    const tempCustomerList: CustomerDataEntry[] = [];
    auditsData.forEach((elem: any) => {
      tempCustomerList.push({
        value: elem.id,
        label: elem.customer,
        audits: elem.sustainabilityAudit,
      });
    });
    setCustomerList(tempCustomerList);
    const filter = await Storage.FILTER_DATA.get();
    const idx = filter.customerIndex >= 0 ? filter.customerIndex : 0;
    setSelectedCustomer(tempCustomerList[idx]);
  };

  useEffect(() => {
    if (selectedCustomer) {
      const parsedAuditList = auditsDataFormat(selectedCustomer.audits);
      setContent(parsedAuditList);
    }
  }, [selectedCustomer]);

  const changeSortAction = (col: number) => {
    setSortSettings({
      column: col,
      order:
        sortSettings?.column === col &&
        sortSettings?.order === SortOrder.Ascending
          ? SortOrder.Descending
          : SortOrder.Ascending,
    });
    setReordering(true);
  };

  const changeSearchParam = (val: string) => {
    setSearchParam(val);
  };

  useEffect(() => {
    if (sortSettings && reordering) {
      setReordering(false);
      const reorderedTable = content.sort((a, b) =>
        sortTableFunction(a, b, sortSettings)
      );
      setContent(reorderedTable);
    }
  }, [sortSettings, reordering, content]);

  const resetTable = () => {
    changeSearchParam("");
    setSortSettings(undefined);
  };

  return (
    <div className={"audits-wrapper" + (darkMode ? " dark" : "")}>
      <div className="filter-row row">
        <FilterRow
          width={width}
          customerData={customerList}
          onChangeCustomerData={(val) => {
            resetTable();
            setSelectedCustomer(val);
          }}
          darkMode={darkMode}
        />
      </div>
      <Container darkTheme={darkMode}>
        <div className="column">
          <SortableTable
            id="audit-list"
            width={width}
            header={headerLine}
            data={content}
            standaloneTable
            isAuditTable
            currentSort={sortSettings}
            onSortChanged={changeSortAction}
            currentFilterKey={searchParam}
            onFilterChanged={changeSearchParam}
            rowsPerPage={25}
            darkMode={darkMode}
            sourceDownload="local"
          />
        </div>
      </Container>
    </div>
  );
};

export default SustainabilityAuditsPage;
